import { createColumnHelper } from '@tanstack/react-table';
import { lt } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { ConvertedCurrencyValue, Dash, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { PaymentPlanDetailResult, PaymentTableRecord } from '@mews-commander/data-access';
import { Tag } from '@optimus-web/core';
import { PaymentTypeCell } from '../Payments/PaymentTypeCell';
import { PaymentsTableBillCell } from '../Payments/PaymentsTableBillCell';
import { PaymentsTableStatusCell } from '../Payments/PaymentsTableStatusCell';
import { PaymentMethodType } from '../PaymentPlans/PaymentMethodType';
import { WithPaymentPlanId } from '../PaymentPlans/types';
import { PaymentPlanDetailsOverviewTableCellActions } from './PaymentPlanDetailsOverviewTableCellActions';

export interface PaymentPlanDetailOverviewTableRecord extends PaymentTableRecord {
    paymentMethodData?: PaymentPlanDetailResult['paymentMethodData'];
    isFuturePayment?: boolean;
    isCancelledPayment?: boolean;
}

const PaymentMethodCell = ({ paymentRecord }: { paymentRecord: PaymentPlanDetailOverviewTableRecord }) => {
    // TODO: PaymentMethodType and PaymentTypeCell should be merged together to diplay same results, FINTECH-3711
    if (paymentRecord.isFuturePayment || paymentRecord.isCancelledPayment) {
        return paymentRecord.paymentMethodData ? (
            <PaymentMethodType paymentMethodData={paymentRecord.paymentMethodData} />
        ) : (
            <Dash />
        );
    }

    return <PaymentTypeCell payment={paymentRecord} />;
};

const columnHelper = createColumnHelper<PaymentPlanDetailOverviewTableRecord>();

export const getPaymentPlanDetailsOverviewTableMapping = ({
    accountId,
    paymentPlanId,
}: WithPaymentPlanId & WithAccountId) => [
    columnHelper.accessor('id', {
        header: () => lt('PaymentMethod'),
        cell: ({ row: { original: paymentRecord } }) => <PaymentMethodCell paymentRecord={paymentRecord} />,
    }),
    columnHelper.accessor('billId', {
        header: () => lt('Bill'),
        cell: ({ row: { original: paymentRecord } }) => (
            <PaymentsTableBillCell accountId={accountId} payment={paymentRecord} />
        ),
    }),
    columnHelper.accessor('state', {
        header: () => lt('Status'),
        cell: ({
            row: {
                original: { transactionDetails, state, isFuturePayment },
            },
        }) => (
            <>
                {isFuturePayment ? (
                    <Tag variant="primary" outline>
                        {lt('PaymentPlanDetailsOverviewTableStatusFuturePayment')}
                    </Tag>
                ) : (
                    <PaymentsTableStatusCell transactionDetails={transactionDetails} state={state} />
                )}
            </>
        ),
    }),
    columnHelper.accessor('createdUtc', {
        header: () => lt('Date'),
        cell: ({
            row: {
                original: { createdUtc },
            },
        }) => <LocalizedDateTimeFromUtc utcString={createdUtc} />,
    }),
    columnHelper.accessor('amount', {
        header: () => lt('Amount'),
        cell: ({
            row: {
                original: { amount },
            },
        }) => <ConvertedCurrencyValue amount={amount} />,
    }),
    columnHelper.accessor('actions', {
        header: () => null,
        meta: {
            align: 'end',
        },
        cell: ({
            row: {
                original: { actions, id, isFuturePayment, amount },
            },
        }) => (
            <PaymentPlanDetailsOverviewTableCellActions
                actions={actions}
                id={id}
                paymentPlanId={paymentPlanId}
                isFuturePayment={!!isFuturePayment}
                simpleAmount={{ currencyCode: amount.currency, value: amount.value }}
                accountId={accountId}
            />
        ),
    }),
];
